import { Link } from 'components/core-ui/Link'
import s from '../partners.modules.scss'
import Table from 'components/core-ui/Table/Table'
import { useEffect, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { getColumns, getFilters } from './helpers'
import { useCountriesQuery, useLegalEntityQuery, useLegalEntityTypesQuery } from '../api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import {
  AddShoppingCart,
  Assignment,
  Groups,
  GroupsOutlined,
  PersonOutlineOutlined
} from '@mui/icons-material'
import { AddActionIcon, EditActionIcon, ViewActionIcon } from 'icons'
import { makeCoreGetRequest } from 'utils/api'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { URLS } from 'components/iam/constants'
import { CircularProgress } from '@mui/material'
import { NewLegalEntityModal } from './NewLegalEntityModal'

const LegalEntities = () => {
  // TODO define interfaces for table items
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [countriesOptions, setCountriesOptions] = useState([])
  const [typesOptions, setTypesOptions] = useState([])
  const [selectedRow, setSelectedRow] = useState<any>()

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => setIsEditModalOpen(true)
  const closeEditModal = (): void => setIsEditModalOpen(false)

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    refetch: refetchCountries
  } = useCountriesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/countries'
    })
  )

  useEffect(() => {
    if (countriesData) {
      setCountriesOptions(
        countriesData.map(country => ({
          label: country.name,
          value: country.name
        }))
      )
    }
  }, [countriesData])

  const {
    data: typesData,
    isLoading: isTypesLoading,
    refetch: refetchTypes
  } = useLegalEntityTypesQuery(showNotification, '/iam/external-partners/legal-entity-type')

  useEffect(() => {
    if (typesData) {
      setTypesOptions(
        typesData.map(type => ({
          label: type.name,
          value: type.id
        }))
      )
    }
  }, [typesData])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(
    () => getFilters(countriesOptions, typesOptions),
    [countriesOptions, typesOptions]
  )

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Partners',
        icon: <Groups width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_PARTNERS_MANAGEMENT)
      },
      {
        label: 'Managers',
        icon: <PersonOutlineOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_MANAGERS)
      },
      {
        label: 'Delegates',
        icon: <GroupsOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_DELEGATES)
      },
      {
        label: 'Assignments',
        icon: <Assignment width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_ASSIGNMENTS)
      },
      {
        label: 'Stores',
        icon: <AddShoppingCart width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_STORES)
      },
      {
        label: 'Add Legal Entity',
        icon: <AddActionIcon />,
        onClick: () => openNewModal()
      }
    ],
    []
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/legal-entities',
          tableFilters,
          searchText,
          searchFields: ['code', 'name'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching legal entities. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const {
    data: legalEntity,
    isLoading: isLegalEntityLoading,
    refetch
  } = useLegalEntityQuery(showNotification, Number(selectedRow), shouldRefreshData)

  const selectRow = (rowId: number): void => {
    setSelectedRow(rowId)
    openEditModal()
    // refetch()
  }

  return (
    <div className={s.mainContainer}>
      <div className={s.breadcrumbsContainer}>
        <Link href="/iam/partners" className={s.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={s.breadcrumbsSeparator}>|</span>
        <span className={s.breadcrumbsCurrent}>Legal Entities</span>
      </div>
      <NewLegalEntityModal
        isModalOpen={isNewModalOpen}
        closeModal={closeNewModal}
        setShouldRefreshData={setShouldRefreshData}
      />
      <NewLegalEntityModal
        isModalOpen={isEditModalOpen}
        closeModal={closeEditModal}
        setShouldRefreshData={setShouldRefreshData}
        editItem={legalEntity}
      />
      {!countriesOptions.length ? (
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      ) : (
        <Table
          hideCheckbox
          hasSearchInput
          orderBy="id"
          order="desc"
          columns={columns}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          shouldResetPage={shouldResetPage}
          setShouldResetPage={setShouldResetPage}
          rowActions={[
            {
              label: 'Details',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#0970E6',
              desktopIcon: <ViewActionIcon />,
              onClick: (_e: React.MouseEvent<HTMLElement>, rowId: number) =>
                navigate(`/iam/externalPartners/legalEntities/${rowId}`)
            },
            {
              label: 'Edit',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#16a085',
              desktopIcon: <EditActionIcon />,
              onClick: (_e, rowId) => selectRow(+rowId)
            }
          ]}
          rowActionsWidth="80px"
          desktopRowActionsWidth="100px"
          toolbarActions={toolbarActions}
          filters={filters}
        />
      )}
    </div>
  )
}

export default LegalEntities
